import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Eye, Mail, Play, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { EmptyInline } from "@/pages/shared";
import { KindBadge, StatusBadge, fmt } from "./sesStatus";
import SesFileDialog from "./SesFileDialog";

const FILTERS = [
  { v: "", key: "ses_filter_all" },
  { v: "open", key: "ses_filter_open" },
  { v: "accepted,cancelled,cancel_requested", key: "ses_filter_accepted" },
  { v: "rejected,uncertain", key: "ses_filter_rejected" },
];

export default function SesFiles({ cfg }) {
  const { t, lang } = useLang();
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("open");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [mailFirst, setMailFirst] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/interfaces/ses/messages", { params: { status: filter || undefined, q: q || undefined, lang } }),
        api.get("/interfaces/ses/messages/stats"),
      ]);
      setRows(r.data); setStats(s.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [filter, q, lang]);
  useEffect(() => { load(); }, [load]);

  const run = async (path, okKey) => {
    setBusy(true);
    try { const { data } = await api.post(path); toast.success(`${t(okKey)} · ${JSON.stringify(data)}`); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const late = (r) => ["incomplete", "ready", "rejected", "uncertain"].includes(r.status) && r.deadline && new Date(r.deadline) < new Date();

  return (
    <div className="space-y-4" data-testid="ses-files">
      <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
        <p className="text-sm text-muted-foreground">{t("ses_files_desc")}</p>
        {!cfg.has_test_password && !cfg.has_live_password && (
          <p className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200" data-testid="ses-no-creds"><AlertTriangle className="h-3.5 w-3.5" />{t("ses_no_creds")}</p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-xl border border-border p-1 bg-background" data-testid="ses-filters">
            {FILTERS.map((f) => (
              <button key={f.v} type="button" onClick={() => setFilter(f.v)} data-testid={`ses-filter-${f.key.replace("ses_filter_", "")}`}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${filter === f.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>
                {t(f.key)}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[12rem] max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("ses_search")} className="pl-8" data-testid="ses-search" />
          </div>
          {stats?.late > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-800 px-3 py-1 text-xs font-semibold" data-testid="ses-late-count"><AlertTriangle className="h-3.5 w-3.5" />{stats.late} · {t("ses_late")}</span>}
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={() => run("/interfaces/ses/check", "ses_check_now")} disabled={busy} data-testid="ses-check-now"><RefreshCw className="h-4 w-4 mr-1" />{t("ses_check_now")}</Button>
            <Button size="sm" onClick={() => run("/interfaces/ses/process", "ses_process_now")} disabled={busy} data-testid="ses-process-now"><Play className="h-4 w-4 mr-1" />{t("ses_process_now")}</Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="max-h-[65vh] overflow-auto cb-scroll">
          {!rows ? null : rows.length === 0 ? <EmptyInline /> : (
            <table className="w-full text-sm" data-testid="ses-table">
              <thead className="sticky top-0 bg-card border-b border-border text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                <tr><th className="px-4 py-3 text-left font-medium">{t("ses_col_file")}</th><th className="px-4 py-3 text-left font-medium">{t("ses_col_content")}</th><th className="px-4 py-3 text-left font-medium">{t("ses_col_status")}</th><th className="px-4 py-3 text-left font-medium">{t("ses_col_created")}</th><th className="px-4 py-3" /></tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {rows.map((r) => (
                  <tr key={r.id} className={`hover:bg-muted/40 ${late(r) ? "bg-red-50/60 dark:bg-red-950/20" : ""}`} data-testid={`ses-row-${r.id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-2"><KindBadge kind={r.kind} /><span className="font-mono text-xs text-foreground break-all">{r.filename}</span></div>
                      {r.mailed?.length > 0 && <p className="mt-1 text-[11px] text-emerald-700 flex items-center gap-1"><Mail className="h-3 w-3" />{t("ses_mailed")} ×{r.mailed.length}</p>}
                    </td>
                    <td className="px-4 py-3 align-top text-foreground max-w-md"><p className="leading-snug">{r.description}</p>
                      {r.error && <p className="mt-1 text-xs text-red-700 font-mono break-all">{r.error}</p>}
                    </td>
                    <td className="px-4 py-3 align-top"><StatusBadge status={r.status} t={t} />
                      {r.status === "ready" && r.send_after && <p className="mt-1 text-[11px] text-sky-700 font-semibold whitespace-nowrap" data-testid={`ses-sendat-${r.id}`}>{t("ses_will_send_at")} {fmt(r.send_after)}</p>}
                      {late(r) && <p className="mt-1 text-[11px] text-red-700 font-semibold">{t("ses_late")}</p>}</td>
                    <td className="px-4 py-3 align-top text-xs text-muted-foreground whitespace-nowrap">{fmt(r.created_at)}<br /><span className="text-[11px]">{t("ses_by")} {r.created_by}</span></td>
                    <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => { setMailFirst(true); setOpenId(r.id); }} title={t("ses_mail")} data-testid={`ses-mail-${r.id}`}><Mail className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => { setMailFirst(false); setOpenId(r.id); }} data-testid={`ses-view-${r.id}`}><Eye className="h-4 w-4 mr-1" />{t("ses_view")}</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {openId && <SesFileDialog id={openId} cfg={cfg} mailFirst={mailFirst} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
